import * as React from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, StatusPill, TextAreaField, Toggle, useRealtime, type DataListColumn } from "@ozpanel/kit";
import {
  IdentityTab,
  MeshTab,
  JoinTab,
  SecretsTab,
  UpdatesTab,
  RecoveryTab,
  HistoryTab,
} from "./ClusterAdmin";
import { CategoryOperations } from "../module/CategoryOperations";
import { FloatingIP } from "./FloatingIP";
import { ClusterStatePanel } from "./ClusterState";
import { PlaneMatrixPanel } from "./PlaneMatrix";
import { ClusterSyncReplicationPanel } from "./SyncReplication";
import { ClusterTimePanel } from "./ClusterTime";
import { DeregisterDialog, EditNodePanel, RegisterNodePanel } from "./NodeRegistry";
import {
  Button,
  ConfirmDialog,
  Panel,
  Section,
  StateView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatRelative,
  useAuth,
  useToast,
} from "@ozpanel/ui";

// The cluster: who is a member, can they reach each other, are they converged, and who holds
// the master key. Every shape read from a live node.
//
// Registered in MODULE_OPS, not given its own nav entry: the `cluster` module already publishes
// a Manifest titled "Cluster", so a static entry with the same label put two of them in the
// sidebar pointing at different routes.

interface Node {
  name: string;
  fqdn: string;
  uuid: string;
  role: string;
  status: string;
  self: boolean;
  public_ip: string;
  real_ip: string;
  /** Every address of the node: kind, and MEASURED reach (cluster_node_addresses). */
  addresses?: NodeAddress[];
  floating_ip: string;
  region: string;
  version: string;
  last_seen: string;
}
interface NodeAddress {
  address: string;
  kind: string; // mesh | public | floating | interface | nat_egress
  reach: string; // cluster | partial | lan | outbound | none | unmeasured
  reached_by?: string;
  verified_at?: string;
}
// What each reach means to an operator: whether other nodes can reach this node at the address.
const reachText: Record<string, string> = {
  cluster: "every node reaches it",
  partial: "some nodes reach it",
  lan: "its own LAN only — never used by other nodes",
  outbound: "sends from it (NAT) — nothing reaches it inbound",
  none: "measured: no node reaches it",
  unmeasured: "not measured yet",
};
interface Peer {
  name: string;
  mesh_addr: string;
  mesh_ok: boolean;
  public_addr: string;
  public_ok: boolean;
  reachable: boolean;
}
/**
 * The liveness JUDGEMENT for one peer, from cluster/liveness.go's Snapshot().
 *
 * /cluster/connectivity sends this under `state`, keyed by node name. The console declared
 * `state?: string` and passed it straight to a Section's `hint`, so an OBJECT was handed to
 * React as a child — "Objects are not valid as a React child", and the Peer connectivity tab
 * went down the moment any peer existed. It could only ever have worked on a single-node
 * cluster, where the map is empty and renders as nothing.
 *
 * It is shown now rather than discarded, because it answers what the instant probe cannot: the
 * mesh/public columns are a ping taken this second, and a peer that is reachable RIGHT NOW after
 * flapping forty times is not the same fact as one that has been up since Tuesday.
 */
interface PeerLiveness {
  up: boolean;
  since?: string;
  down_count?: number;
  up_count?: number;
  judged?: boolean;
}
interface ConvNode {
  node: string;
  applied_gen: number;
  behind_by: number;
  never_applied?: boolean;
  diverged: boolean;
  status: string;
  self: boolean;
  reported_at: string;
}
interface Master {
  has_master: boolean;
  am_i_leader: boolean;
  i_hold_key: boolean;
  verified: boolean;
  node_name: string;
  node_uuid: string;
  declared_at: string;
  this_node: string;
  note?: string;
}

/**
 * The cluster screen HEARS a role change instead of discovering it on its next poll.
 *
 * A page left open through a failover showed "node10 primary" for as long as its refetch
 * interval while every daemon and the API said node100 (2026-09-09). The state machine emits
 * cluster/role_changed over /realtime/subscribe whenever the declared primary or the per-plane
 * agreement changes; every cluster query on this screen is invalidated the moment one arrives, so
 * what is on screen is what the cluster says, not what it said when the tab was opened.
 */
function useRoleChangeInvalidation() {
  const qc = useQueryClient();
  const { events } = useRealtime();
  const seen = React.useRef(0);
  React.useEffect(() => {
    const roleEvents = events.filter((e) => e.module === "cluster" && e.kind === "role_changed" && e.seq > seen.current);
    if (roleEvents.length === 0) return;
    seen.current = Math.max(...roleEvents.map((e) => e.seq));
    for (const key of ["cluster-nodes", "cluster-state", "cluster-master", "cluster-roles", "cluster-succession"]) {
      void qc.invalidateQueries({ queryKey: [key] });
    }
  }, [events, qc]);
}

function NodesTab() {
  const { client } = useAuth();
  const qc = useQueryClient();
  useRoleChangeInvalidation();
  const { notify } = useToast();
  const [open, setOpen] = React.useState<string | null>(null);
  const [evicting, setEvicting] = React.useState<Node | null>(null);
  const [reason, setReason] = React.useState("");
  const [ackLoss, setAckLoss] = React.useState(false);
  const [evictResult, setEvictResult] = React.useState<{ node: string; ok: boolean; data?: EvictAnswer; error?: string } | null>(null);
  const [evictBusy, setEvictBusy] = React.useState(false);
  // The registry verbs (NodeRegistry.tsx): register, edit, deregister.
  const [registering, setRegistering] = React.useState(false);
  const [editing, setEditing] = React.useState<string | null>(null);
  const [deregistering, setDeregistering] = React.useState<Node | null>(null);

  const q = useQuery({
    queryKey: ["cluster-nodes"],
    queryFn: () => client.apiFetch<{ items: Node[] | null; total: number }>("/cluster/nodes"),
    refetchInterval: 30000,
  });
  // Eviction is refused by the server unless THIS node is the verified master, so the control is
  // only offered where it can work. A button that can only ever answer 403 teaches an operator to
  // distrust the whole screen.
  const master = useQuery({
    queryKey: ["cluster-master"],
    queryFn: () => client.apiFetch<{ node_name?: string; am_i_leader?: boolean; verified?: boolean }>("/cluster/master"),
    retry: false,
  });
  const canEvict = master.data?.am_i_leader === true && master.data?.verified === true;

  const columns: DataListColumn<Node>[] = [
    {
      key: "name",
      label: "Node",
      sortable: true,
      render: (n) => (
        <span>
          {n.name}
          {n.self && (
            <span className="ml-2 inline-block align-middle">
              <StatusPill tone="accent">this node</StatusPill>
            </span>
          )}
        </span>
      ),
    },
    { key: "role", label: "Workload role", sortable: true },
    {
      key: "status",
      label: "Status",
      sortable: true,
      // cluster.go markStatus sends "online" (heartbeat within 2 minutes) or "offline". This compared
      // against "active", which the server never sends, so every node was painted as a warning.
      render: (n) => <StatusPill tone={n.status === "online" ? "ok" : "warn"}>{n.status}</StatusPill>,
    },
    { key: "public_ip", label: "Public IP", sortable: true, render: (n) => <span className="font-mono text-[12px]">{n.public_ip}</span> },
    {
      key: "addresses",
      label: "Addresses",
      // Every address with its kind and MEASURED reach: each node asks its peers to dial its addresses
      // back and counts only answers carrying its own certificate.
      render: (n) =>
        n.addresses && n.addresses.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            {n.addresses.map((a) => (
              <span
                key={a.kind + a.address}
                className="font-mono text-[12px] whitespace-nowrap"
                title={`${reachText[a.reach] ?? a.reach}${a.reached_by ? " (reached by " + a.reached_by + ")" : ""}${a.verified_at ? " · proven " + a.verified_at : ""}`}
              >
                {a.address} <span className="opacity-60">{a.kind}</span>{" "}
                <StatusPill tone={a.reach === "cluster" ? "ok" : a.reach === "lan" || a.reach === "outbound" ? "neutral" : "warn"}>{a.reach}</StatusPill>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-[12px] opacity-50">—</span>
        ),
    },
    { key: "version", label: "Version", sortable: true, render: (n) => <span className="font-mono text-[12px]">{n.version}</span> },
    {
      key: "last_seen",
      label: "Last seen",
      sortable: true,
      // A node that has not reported is UNKNOWN, not down — the distinction that stops a
      // reporter turning "nobody asked recently" into "it is dead".
      render: (n) => (n.last_seen ? formatRelative(n.last_seen) : <span className="text-muted">never reported</span>),
    },
  ];

  return (
    <>
    {/* The interlocked answer FIRST: a member list says who is registered, not who can take over.
        Those were computed separately for a long time, and four subsystems each acted on the wrong
        one — see ClusterState.tsx. */}
    <ClusterStatePanel />
    {/* The transfer watch, directly under the state it completes: /cluster/state says WHO should be
        primary, this says whether every service on every node has actually got there. */}
    <PlaneMatrixPanel />
    {/* Who may be promoted is a PostgreSQL fact the plane matrix cannot show: only the recorded
        synchronous standby holds every acknowledged commit. Every node's own answer (sync state,
        pooler, juicefs fence) via /cluster/sync-replication, reduced to one verdict. */}
    <ClusterSyncReplicationPanel />
    {/* The clocks every staleness limit, lease and takeover check above compares: per node, what
        disciplines it and its measured offset to each peer (/clock/cluster). A skewed node reads as
        a stolen lease or a silent peer — this is the only place it reads as a clock. */}
    <ClusterTimePanel />
    <Section
      title="Members"
      hint={q.data ? `${q.data.total} node(s)` : undefined}
      bodyClassName="p-0"
      actions={
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setRegistering((r) => !r)}>
            {registering ? "Close register" : "Register a node"}
          </Button>
          <Button size="sm" variant="accent" asChild>
            <Link to="/nodes/add">Add a node</Link>
          </Button>
        </div>
      }
    >
      {registering && (
        <RegisterNodePanel existing={(q.data?.items ?? []).map((n) => n.name)} onClose={() => setRegistering(false)} />
      )}
      {!canEvict && (
        <div className="border-b border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">
          Removing a node can only be done from the cluster master
          {master.data?.node_name ? <> — currently <span className="font-mono">{master.data.node_name}</span></> : null}.
        </div>
      )}
      <div className="p-4">
        <DataList
          expandedKey={open}
          renderExpanded={(n: Node) => (
            <>
              {editing === n.name && <EditNodePanel name={n.name} onClose={() => setEditing(null)} />}
              <NodeDetail name={n.name} />
            </>
          )}
          rowActions={(n: Node) => (
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setOpen(open === n.name ? null : n.name)}>
                {open === n.name ? "Close" : "Details"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(n.name);
                  setOpen(n.name);
                }}
              >
                Edit
              </Button>
              {/* The server refuses to deregister the node answering; the reason is on the button. */}
              <Button
                size="sm"
                variant="ghost"
                disabled={n.self}
                title={n.self ? "The server refuses to deregister the node answering this console. Ask another node's console." : "Delete its registry row only — not an eviction"}
                onClick={() => setDeregistering(n)}
              >
                Deregister
              </Button>
              {canEvict && !n.self && (
                <Button size="sm" variant="danger" onClick={() => { setReason(""); setAckLoss(false); setEvicting(n); }}>Remove</Button>
              )}
            </div>
          )}
          items={q.data?.items ?? []}
          columns={columns}
          getRowKey={(n) => n.uuid || n.name}
          loading={q.isLoading}
          error={q.error}
          errorContext="the node registry"
          emptyMessage="No nodes registered."
        />
      </div>
    </Section>

    <DeregisterDialog
      node={deregistering}
      onClose={() => setDeregistering(null)}
      canEvict={canEvict}
      masterName={master.data?.node_name}
    />

    {evictResult && <EvictReport result={evictResult} onDismiss={() => setEvictResult(null)} />}

    <ConfirmDialog
      open={!!evicting}
      onOpenChange={(o) => !o && setEvicting(null)}
      title={`Remove ${evicting?.name ?? ""} from the cluster?`}
      confirmLabel={evictBusy ? "Removing…" : "Remove it"}
      destructive
      pending={evictBusy}
      confirmDisabled={reason.trim().length < 4 || evictBusy}
      body={
        <div className="space-y-3 text-[12.5px]">
          <p>
            Its certificates are revoked and it is tombstoned, so it cannot re-register even after a
            reboot. Its database rows, storage membership, Redis and mesh entries are removed and every
            gate is re-derived. The declared primary and the master cannot be removed — hand them over first.
          </p>
          {/* The two facts that decide whether this is safe, and neither is visible from the row. */}
          <p className="text-warn">
            Anything this node alone was holding goes with it. A pinned app keeps its DNS record with
            nowhere to serve it until the pin is moved.
          </p>
          <TextAreaField
            label="Reason (recorded on the tombstone, visible under History)"
            value={reason}
            onChange={setReason}
            rows={2}
            placeholder="e.g. machine decommissioned"
          />
          <Toggle
            checked={ackLoss}
            onChange={setAckLoss}
            label="Proceed even where its own copy of shared data cannot be read"
            help="Without this, the removal refuses when the node's storage state cannot be measured. With it, anything the node wrote while cut off from the others is accepted as lost — right only if it has been DOWN throughout. Files whose ONLY copy is on it are refused regardless."
            consequence={ackLoss ? "Recorded on the tombstone as acknowledged data loss." : undefined}
          />
        </div>
      }
      onConfirm={() => {
        const n = evicting;
        if (!n) return;
        setEvictBusy(true);
        void client
          .apiFetch<EvictAnswer>(`/cluster/nodes/${encodeURIComponent(n.name)}/evict`, {
            method: "POST",
            body: JSON.stringify({ reason: reason.trim(), acknowledge_data_loss: ackLoss }),
          })
          .then((d) => {
            setEvictResult({ node: n.name, ok: true, data: d });
            notify({ kind: d.complete ? "success" : "error", title: d.complete ? `${n.name} removed` : `${n.name} removed incompletely — read the report` });
            void qc.invalidateQueries({ queryKey: ["cluster-nodes"] });
            void qc.invalidateQueries({ queryKey: ["cluster-evictions"] });
          })
          .catch((e: unknown) => setEvictResult({ node: n.name, ok: false, error: e instanceof Error ? e.message : String(e) }))
          .finally(() => {
            setEvictBusy(false);
            setEvicting(null);
          });
      }}
    />
    </>
  );
}

/** What POST /cluster/nodes/{name}/evict answers: what it did, what it proved, what it could not do. */
interface EvictAnswer {
  complete: boolean;
  evicted?: { done?: string[] | null; failed?: string[] | null; pending?: string[] | null; tombstoned?: boolean; resumed?: boolean };
  proof?: Record<string, unknown>;
  phases?: unknown[] | null;
  peers?: { node: string; ok: boolean; error?: string; failed?: string[] }[] | null;
  note?: string;
}

/** The server's own account of the removal — never reduced to a toast, because the half that says
 *  what could NOT be closed is the half that needs reading. */
function EvictReport({ result, onDismiss }: { result: { node: string; ok: boolean; data?: EvictAnswer; error?: string }; onDismiss: () => void }) {
  const d = result.data;
  const list = (title: string, xs?: string[] | null, tone = "text-muted") =>
    xs && xs.length > 0 ? (
      <div>
        <div className="text-[11.5px] tracking-wide text-muted uppercase">{title}</div>
        <ul className={`mt-1 list-disc pl-5 text-[12px] leading-relaxed ${tone}`}>
          {xs.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      </div>
    ) : null;
  return (
    <Panel className="mt-4 p-4">
      <div className="flex items-center gap-2 text-[13px]">
        <span className="font-medium">Removal of {result.node}</span>
        {result.ok ? (
          <StatusPill tone={d?.complete ? "ok" : "warn"}>{d?.complete ? "complete — proven" : "incomplete"}</StatusPill>
        ) : (
          <StatusPill tone="bad">refused</StatusPill>
        )}
        <button type="button" className="ml-auto text-[11.5px] text-muted hover:text-fg" onClick={onDismiss}>
          dismiss
        </button>
      </div>
      {result.error && <p className="mt-2 text-[12.5px] leading-relaxed text-crit">{result.error}</p>}
      {d && (
        <div className="mt-3 space-y-3">
          {list("Failed", d.evicted?.failed, "text-crit")}
          {list("Done", d.evicted?.done)}
          {list("Not closed by any automated path", d.evicted?.pending, "text-warn")}
          {d.peers && d.peers.length > 0 && (
            <div>
              <div className="text-[11.5px] tracking-wide text-muted uppercase">Other nodes' local steps</div>
              <ul className="mt-1 text-[12px]">
                {d.peers.map((p) => (
                  <li key={p.node}>
                    <span className="font-mono">{p.node}</span>{" "}
                    <StatusPill tone={p.ok ? "ok" : "bad"}>{p.ok ? "done" : "failed"}</StatusPill>{" "}
                    <span className="text-muted">{p.error ?? (p.failed ?? []).join("; ")}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {d.proof && (
            <details>
              <summary className="cursor-pointer text-[12px] text-muted">The proof (re-read after the removal)</summary>
              <pre className="mt-1 max-h-56 overflow-auto rounded bg-bg p-2 font-mono text-[11px] whitespace-pre-wrap">{JSON.stringify(d.proof, null, 1)}</pre>
            </details>
          )}
          {d.note && <p className="text-[11.5px] leading-relaxed text-muted">{d.note}</p>}
        </div>
      )}
    </Panel>
  );
}

// One node, opened beneath its own row: what it reports about itself, and — on demand — what every
// other node sees of it. The second half used to read GET /cluster/network/{node}/transport, a route
// that does not exist (the path segment there is a SERVICE key and it only takes a POST); which
// network each service uses is a cluster-wide setting and lives under Mesh.
function NodeDetail({ name }: { name: string }) {
  const { client } = useAuth();

  const q = useQuery({
    queryKey: ["cluster-node", name],
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/cluster/nodes/${encodeURIComponent(name)}`),
    retry: false,
  });
  // Asks every peer over the node-to-node channel — a deliberate act per click, never a poll.
  const [askWitness, setAskWitness] = React.useState(false);
  const witness = useQuery({
    queryKey: ["cluster-witness", name],
    enabled: askWitness,
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/cluster/nodes/${encodeURIComponent(name)}/witness`),
    retry: false,
  });

  const d = q.data ?? {};
  const row = (k: string, v: unknown) => (
    <div key={k} className="flex justify-between gap-4 border-b border-line py-1 text-[12.5px]">
      <span className="text-muted">{k.replace(/_/g, " ")}</span>
      <span className="text-right font-mono text-[11.5px] break-all">{v == null || v === "" ? "—" : String(v)}</span>
    </div>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">What it reports</div>
        <StateView loading={q.isLoading} error={q.error} errorContext="this node">
          {Object.entries(d)
            .filter(([, v]) => v === null || typeof v !== "object")
            .map(([k, v]) => row(k, v))}
        </StateView>
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted">What its peers see</span>
          <Button size="sm" variant="ghost" disabled={witness.isFetching}
            onClick={() => (askWitness ? void witness.refetch() : setAskWitness(true))}>
            {witness.isFetching ? "Asking…" : askWitness ? "Ask again" : "Ask every peer"}
          </Button>
        </div>
        {!askWitness ? (
          <p className="text-[12px] leading-relaxed text-muted">
            Each other node is asked, over the node-to-node channel, whether it can reach {name} and on which
            address — the corroborated view a failover decision uses, rather than this node's alone.
          </p>
        ) : (
          <StateView loading={witness.isLoading} error={witness.error} errorContext="the witnesses">
            <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-bg px-3 py-2 font-mono text-[11px]">
              {JSON.stringify(witness.data, null, 1)}
            </pre>
          </StateView>
        )}
      </div>
    </div>
  );
}

function ConnectivityTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-connectivity"],
    queryFn: () =>
      client.apiFetch<{
        peers: Peer[] | null;
        state?: Record<string, PeerLiveness> | null;
        self_connectivity?: boolean;
        dns?: string;
      }>("/cluster/connectivity"),
    refetchInterval: 30000,
  });

  const liveness = q.data?.state ?? {};
  const columns: DataListColumn<Peer>[] = [
    { key: "name", label: "Peer", sortable: true },
    {
      // Both paths matter and they fail for different reasons: the mesh is how replication and
      // gluster talk, the public address is how mail and the web arrive. One badge for "peer
      // reachable" would hide a node that is up but off the VPN — the case where every service
      // looks healthy and nothing replicates.
      key: "mesh",
      label: "Mesh (VPN)",
      value: (p) => (p.mesh_ok ? "ok" : "down"),
      render: (p) => (
        <span className="flex items-center gap-2">
          <StatusPill tone={p.mesh_ok ? "ok" : "bad"}>{p.mesh_ok ? "reachable" : "unreachable"}</StatusPill>
          <span className="font-mono text-[11.5px] text-muted">{p.mesh_addr}</span>
        </span>
      ),
    },
    {
      key: "public",
      label: "Public",
      value: (p) => (p.public_ok ? "ok" : "down"),
      render: (p) => (
        <span className="flex items-center gap-2">
          <StatusPill tone={p.public_ok ? "ok" : "bad"}>{p.public_ok ? "reachable" : "unreachable"}</StatusPill>
          <span className="font-mono text-[11.5px] text-muted">{p.public_addr}</span>
        </span>
      ),
    },
    {
      // The judgement, beside the instant probe. `judged: false` is NOT "up" — it means not
      // enough samples to say — and it is shown as its own state rather than folded into either,
      // because reporting an unmeasured peer as healthy is the failure this codebase names most.
      key: "liveness",
      label: "Liveness",
      value: (p) => (liveness[p.name]?.judged ? (liveness[p.name]?.up ? "up" : "down") : "unjudged"),
      render: (p) => {
        const l = liveness[p.name];
        if (!l) return <span className="text-muted">—</span>;
        return (
          <span className="flex items-center gap-2">
            <StatusPill tone={!l.judged ? "neutral" : l.up ? "ok" : "bad"}>
              {!l.judged ? "not yet judged" : l.up ? "up" : "down"}
            </StatusPill>
            <span className="text-[11.5px] text-muted">
              {l.since ? `since ${new Date(l.since).toLocaleString()}` : ""}
              {(l.down_count ?? 0) > 0 ? ` · ${l.down_count} outage(s)` : ""}
            </span>
          </span>
        );
      },
    },
  ];

  return (
    <Section
      title="Peer connectivity"
      hint={
        q.data?.dns
          ? `DNS: ${q.data.dns}`
          : "Mesh and public are probed right now; Liveness is the judgement held over time."
      }
      bodyClassName="p-0"
    >
      <div className="p-4">
        <DataList
          items={q.data?.peers ?? []}
          columns={columns}
          getRowKey={(p) => p.name}
          loading={q.isLoading}
          error={q.error}
          errorContext="peer connectivity"
          emptyMessage="No peers — this is a single-node cluster."
          emptyHint="Replication, shared storage and failover all need a second node; a lone node has none of them."
        />
      </div>
    </Section>
  );
}

function ConvergenceTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-convergence"],
    queryFn: () =>
      client.apiFetch<{ nodes: ConvNode[] | null; converged: boolean; target_gen: number }>("/cluster/convergence"),
    refetchInterval: 20000,
  });

  const columns: DataListColumn<ConvNode>[] = [
    {
      key: "node",
      label: "Node",
      sortable: true,
      render: (n) => (
        <span>
          {n.node}
          {n.self && (
            <span className="ml-2 inline-block align-middle">
              <StatusPill tone="accent">this node</StatusPill>
            </span>
          )}
        </span>
      ),
    },
    { key: "applied_gen", label: "Applied gen", sortable: true, value: (n) => n.applied_gen ?? 0 },
    {
      key: "behind_by",
      label: "Behind by",
      sortable: true,
      value: (n) => n.behind_by ?? 0,
      render: (n) =>
        n.behind_by > 0 ? <StatusPill tone="warn">{n.behind_by} generation(s)</StatusPill> : <span className="text-muted">up to date</span>,
    },
    {
      key: "diverged",
      label: "Config",
      value: (n) => (n.diverged ? "diverged" : "matches"),
      // Behind and DIVERGED are different faults. Behind catches up on its own; diverged means
      // this node rendered something different from the same inputs and will not converge by
      // waiting.
      render: (n) => <StatusPill tone={n.diverged ? "bad" : "ok"}>{n.diverged ? "diverged" : "matches"}</StatusPill>,
    },
    { key: "reported_at", label: "Reported", sortable: true, render: (n) => (n.reported_at ? formatRelative(n.reported_at) : "—") },
  ];

  return (
    <Section
      title="Convergence"
      hint={q.data ? (q.data.converged ? "converged" : "NOT converged") : undefined}
      bodyClassName="p-0"
    >
      <div className="p-4">
        {q.data && !q.data.converged && (
          <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
            At least one node has not applied generation {q.data.target_gen}. Until it does, that node is serving
            an older configuration — which is not visible from the node itself, because everything there is
            running normally against what it last applied.
          </Panel>
        )}
        <DataList
          items={q.data?.nodes ?? []}
          columns={columns}
          getRowKey={(n) => n.node}
          loading={q.isLoading}
          error={q.error}
          errorContext="convergence state"
          emptyMessage="No convergence reports."
        />
      </div>
    </Section>
  );
}

function MasterTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-master"],
    queryFn: () => client.apiFetch<Master>("/cluster/master"),
  });
  const d = q.data;

  return (
    <Section
      title="Signing authority (master / CA holder)"
      hint="Holds the cluster CA key: signs declarations, certificates and releases. NOT the workload primary — that runs PostgreSQL, Redis and the arbitrator and is shown under Cluster state. The two are decoupled on purpose: a wrongly moved master is corrected by claiming it back; two nodes accepting writes is not."
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="the master record">
        {d && (
          <>
            {/* Three separate facts. A cluster can have a DECLARED master that is not verified,
                or a master whose key this node does not hold — and each has a different
                consequence, so collapsing them into "leader: yes" hides which one is true. */}
            <div className="mb-3 flex flex-wrap gap-1.5">
              <StatusPill tone={d.has_master ? "ok" : "bad"}>
                {d.has_master ? `master declared: ${d.node_name}` : "no master declared"}
              </StatusPill>
              <StatusPill tone={d.verified ? "ok" : "warn"}>
                {d.verified ? "verified" : "not verified"}
              </StatusPill>
              <StatusPill tone={d.am_i_leader ? "accent" : "neutral"}>
                {d.am_i_leader ? "this node is the leader" : `this node is ${d.this_node}`}
              </StatusPill>
              <StatusPill tone={d.i_hold_key ? "ok" : "neutral"}>
                {d.i_hold_key ? "holds the CA key" : "does not hold the CA key"}
              </StatusPill>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12.5px]">
              <dt className="text-muted">Master node</dt>
              <dd className="font-mono">{d.node_name || "—"}</dd>
              <dt className="text-muted">UUID</dt>
              <dd className="break-all font-mono text-[11.5px]">{d.node_uuid || "—"}</dd>
              <dt className="text-muted">Declared</dt>
              <dd>{d.declared_at ? formatRelative(d.declared_at) : "—"}</dd>
            </dl>
            {d.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{d.note}</p>}
          </>
        )}
      </StateView>
    </Section>
  );
}

export function ClusterOps() {
  // Observation first, then the things you DO. The first four tabs answer "what is true right
  // now"; the rest are administrative surfaces that were previously reachable only by curl.
  return (
    <div>
      <Tabs defaultValue="nodes">
        <TabsList>
          <TabsTrigger value="nodes">Nodes</TabsTrigger>
          <TabsTrigger value="connectivity">Connectivity</TabsTrigger>
          <TabsTrigger value="convergence">Convergence</TabsTrigger>
          <TabsTrigger value="master">Master</TabsTrigger>
          <TabsTrigger value="identity">Identity &amp; CA</TabsTrigger>
          <TabsTrigger value="mesh">Mesh</TabsTrigger>
          <TabsTrigger value="join">Join</TabsTrigger>
          <TabsTrigger value="secrets">Secrets</TabsTrigger>
          <TabsTrigger value="updates">Updates</TabsTrigger>
          <TabsTrigger value="recovery">Recovery</TabsTrigger>
          {/* Everything the cluster can be TOLD to do, in one place. Redis failover, Galera
              bootstrap, GlusterFS brick work and mesh repair are all cluster management to the
              person doing them, and each was reachable only from its own module page — which
              required knowing which module owned the verb before you could go looking. */}
          {/* A node-lifecycle operation like Join: it needs hub credentials, it runs as a
              resumable job, and it changes the address every other tab's data is keyed to. */}
          <TabsTrigger value="floating">Floating IP</TabsTrigger>
          <TabsTrigger value="operations">Operations</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="nodes">
          <NodesTab />
        </TabsContent>
        <TabsContent value="connectivity">
          <ConnectivityTab />
        </TabsContent>
        <TabsContent value="convergence">
          <ConvergenceTab />
        </TabsContent>
        <TabsContent value="master">
          <MasterTab />
        </TabsContent>
        <TabsContent value="identity">
          <IdentityTab />
        </TabsContent>
        <TabsContent value="mesh">
          <MeshTab />
        </TabsContent>
        <TabsContent value="join">
          <JoinTab />
        </TabsContent>
        <TabsContent value="secrets">
          <SecretsTab />
        </TabsContent>
        <TabsContent value="updates">
          <UpdatesTab />
        </TabsContent>
        <TabsContent value="recovery">
          <RecoveryTab />
        </TabsContent>
        <TabsContent value="floating">
          <FloatingIP />
        </TabsContent>

        <TabsContent value="operations">
          <div className="space-y-4">
            <CategoryOperations
              category="nodes"
              title="Cluster operations"
              hint="Everything the cluster and node modules can be told to do."
            />
            <CategoryOperations
              category="storage"
              title="Storage operations"
              hint="Volumes, bricks, mounts and the shared filesystem every node reads."
            />
            <CategoryOperations
              category="database"
              title="Database & replication operations"
              hint="Galera, PostgreSQL, PgBouncer, Redis and the arbitrator — the state that decides whether this node accepts writes."
            />
            <CategoryOperations
              category="network"
              title="Network operations"
              hint="Firewall, mesh and the transport every other service rides."
            />
          </div>
        </TabsContent>

        <TabsContent value="history">
          <HistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
